import { makeStyles, tokens } from '@fluentui/react-components';
import type { ICardData } from '@moko/utils';
import EntityCard from './EntityCard';
import type { IStatusOption } from '../StatusBadge';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  listHorizontal: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  cardChrome: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '10px',
    padding: '14px 16px',
    cursor: 'pointer',
    transitionProperty: 'box-shadow, border-color',
    transitionDuration: '150ms',
  },
  cardRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardFooterStart: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  cardAction: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  cardFooterEnd: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: tokens.spacingVerticalS,
  },
  cardChromeHorizontal: {
    flex: '1 1 0',
    minWidth: '180px',
    // Cap so 1–2 cards don't balloon to fill the row.
    maxWidth: '350px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    // Subtle hover so the user can see which card they're about to pick.
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: tokens.shadow4,
    },
  },
});

const SELECTED_STYLE: React.CSSProperties = {
  borderColor: tokens.colorBrandStroke1,
  boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
};

interface CardListProps {
  cards: ICardData[];
  onCardClick?: (id: number, event?: React.MouseEvent<HTMLElement>) => void;
  selectable?: boolean;
  selectedId?: number | null;
  /** Multi-select: set of currently selected card ids. When provided,
   * cards render with a checkbox instead of a radio. */
  selectedIds?: Set<number>;
  onSelect?: (id: number) => void;
  statuses?: readonly IStatusOption[];
  /** When true, cards lay out in a horizontal row (wrapping) with equal
   * widths instead of a vertical stack. Used for the inline "recent" row. */
  horizontal?: boolean;
  /** Optional content rendered at the bottom of each card (e.g. a
   * "Fetch details" link). Receives the card id. */
  renderCardFooter?: (id: number) => React.ReactNode;
  renderCardAction?: (id: number) => React.ReactNode;
  showFieldLabels?: boolean;
}

const CardList = ({
  cards,
  onCardClick,
  selectable,
  selectedId,
  selectedIds,
  onSelect,
  statuses,
  horizontal = false,
  renderCardFooter,
  renderCardAction,
  showFieldLabels = true,
}: CardListProps) => {
  const styles = useStyles();
  const isMulti = selectedIds !== undefined;

  return (
    <div className={horizontal ? styles.listHorizontal : styles.list}>
      {cards.map((card) => {
        const isSelected = isMulti
          ? selectedIds!.has(card.id)
          : selectedId === card.id;
        return (
          <div
            key={card.id}
            className={
              horizontal
                ? `${styles.cardChrome} ${styles.cardChromeHorizontal}`
                : styles.cardChrome
            }
            style={isSelected ? SELECTED_STYLE : undefined}
          >
            <div className={styles.cardRow}>
              <div className={styles.cardBody}>
                <EntityCard
                  data={card}
                  onClick={onCardClick}
                  selectable={selectable}
                  multi={isMulti}
                  selected={isSelected}
                  onSelect={onSelect}
                  statuses={statuses}
                  showFieldLabels={showFieldLabels}
                />
              </div>
              {renderCardAction && (
                <div className={styles.cardAction}>
                  {renderCardAction(card.id)}
                </div>
              )}
            </div>
            {renderCardFooter && (
              <div
                className={
                  horizontal ? styles.cardFooterStart : styles.cardFooterEnd
                }
              >
                {renderCardFooter(card.id)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CardList;
